import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import ErrorState from './ErrorState';
import Modal from './Modal';
import StudentForm from './StudentForm';
import StudentSearchSelect from './StudentSearchSelect';
import { diagnoseMissingGroups } from '../utils/diagnosticHelper';
import './EnrollmentForm.css';

const EnrollmentForm = ({ initialData, onSubmit, onCancel, isLoading }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Estado para el paso actual del flujo
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  // Estado para el modal de nuevo alumno
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  // Estado para confirmación de override de categoría por edad
  const [ageOverrideConfirm, setAgeOverrideConfirm] = useState(null);

  const createStudentMutation = useMutation(
    (data) => api.post('/students', data),
    {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries('students');
        await queryClient.invalidateQueries('students-search');
        const newStudent = response.data;
        setValue('student_id', newStudent.id);
        setShowAddStudentModal(false);
        toast.success(`Alumno "${newStudent.first_name} ${newStudent.last_name}" creado y seleccionado.`);
      },
      onError: (err) => {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach(e => toast.error(`${e.loc?.[1] || ''}: ${e.msg}`));
        } else {
          toast.error(detail || 'Error al crear alumno');
        }
      },
    }
  );

  const formatTodayLocal = () => {
    const t = new Date();
    const year = t.getFullYear();
    const month = String(t.getMonth() + 1).padStart(2, '0');
    const day = String(t.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función para formatear fechas sin problemas de zona horaria
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función helper para calcular edad desde fecha de nacimiento
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Función helper para obtener año de nacimiento
  const getBirthYear = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    return birth.getFullYear();
  };

  // Función helper para validar compatibilidad de categoría
  const isCategoryCompatible = (student, level) => {
    if (!student || !level) return { compatible: true, reason: null };
    
    if (level.category_type === 'skill') {
      return { compatible: true, reason: null };
    }
    
    if (level.category_type === 'age') {
      const studentAge = calculateAge(student.birth_date);
      const studentBirthYear = getBirthYear(student.birth_date);
      
      if (level.min_birth_year || level.max_birth_year) {
        if (studentBirthYear) {
          const minYear = level.min_birth_year || 1900;
          const maxYear = level.max_birth_year || new Date().getFullYear();
          if (studentBirthYear < minYear || studentBirthYear > maxYear) {
            return {
              compatible: false,
              reason: `Año de nacimiento ${studentBirthYear} no corresponde a esta categoría (${minYear}${level.max_birth_year ? `-${level.max_birth_year}` : ''})`
            };
          }
        }
      }
      
      if (level.min_age || level.max_age) {
        if (studentAge !== null) {
          const minAge = level.min_age || 0;
          const maxAge = level.max_age || 100;
          if (studentAge < minAge || studentAge > maxAge) {
            return {
              compatible: false,
              reason: `Edad ${studentAge} años no corresponde a esta categoría (${minAge}${level.max_age ? `-${maxAge} años` : '+ años'})`
            };
          }
        }
      }
    }
    
    return { compatible: true, reason: null };
  };

  // Función helper para formatear información de categoría
  const formatCategoryInfo = (level) => {
    if (!level || level.category_type === 'skill') {
      return null;
    }
    
    const parts = [];
    
    if (level.min_birth_year || level.max_birth_year) {
      if (level.min_birth_year === level.max_birth_year) {
        parts.push(`Año: ${level.min_birth_year}`);
      } else {
        parts.push(`Años: ${level.min_birth_year || '?'}${level.max_birth_year ? `-${level.max_birth_year}` : '+'}`);
      }
    }
    
    if (level.min_age || level.max_age) {
      if (level.min_age === level.max_age) {
        parts.push(`Edad: ${level.min_age} años`);
      } else {
        parts.push(`Edad: ${level.min_age || '?'}${level.max_age ? `-${level.max_age} años` : '+ años'}`);
      }
    }
    
    return parts.length > 0 ? parts.join(' | ') : null;
  };

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: initialData ? {
      ...initialData,
      discipline_id: '',
      level_id: initialData.level_id || '',
      start_date: formatDateForInput(initialData.start_date),
      end_date: formatDateForInput(initialData.end_date),
    } : {
      student_id: '',
      discipline_id: '',
      level_id: '',
      group_id: '',
      start_date: '',
      end_date: '',
      period_type: 'monthly',
      monthly_fee: '',
      has_scholarship: false,
      scholarship_name: '',
      discount_type: 'percentage',
      discount_percentage: '',
      discount_amount: '',
      notes: '',
      collect_payment_now: true,
      payment_date: formatTodayLocal(),
      payment_due_date: '',
      payment_amount: '',
      payment_paid_amount: '',
      payment_method: 'cash',
      payment_reference_number: '',
    },
  });

  const selectedStudentId = watch('student_id');
  const selectedDisciplineId = watch('discipline_id');
  const selectedLevelId = watch('level_id');
  const selectedGroupId = watch('group_id');
  const monthlyFee = watch('monthly_fee');
  const hasScholarship = watch('has_scholarship');
  const discountType = watch('discount_type');
  const discountPercentage = watch('discount_percentage');
  const periodType = watch('period_type');
  const startDate = watch('start_date');
  const discountAmount = watch('discount_amount');
  const collectPaymentNow = watch('collect_payment_now');

  // Auto-calcular fecha fin solo al crear matrícula (no al editar: si no, se pisan
  // start/end guardados en BD con el cálculo por period_type y al reabrir el formulario
  // parece que "no se guardaron" las fechas).
  useEffect(() => {
    if (initialData) {
      return;
    }
    if (startDate && periodType) {
      const start = new Date(startDate + 'T00:00:00'); // Evitar problemas de zona horaria
      let end = new Date(start);
      
      switch (periodType) {
        case 'monthly':
          // Sumar 1 mes y restar 1 día para el último día del período
          end.setMonth(end.getMonth() + 1);
          end.setDate(end.getDate() - 1);
          break;
        case 'quarterly':
          // Sumar 3 meses y restar 1 día
          end.setMonth(end.getMonth() + 3);
          end.setDate(end.getDate() - 1);
          break;
        case 'annual':
          // Sumar 1 año y restar 1 día
          end.setFullYear(end.getFullYear() + 1);
          end.setDate(end.getDate() - 1);
          break;
        default:
          end.setMonth(end.getMonth() + 1);
          end.setDate(end.getDate() - 1);
      }
      
      const formattedEndDate = formatDateForInput(end);
      setValue('end_date', formattedEndDate);
    }
  }, [startDate, periodType, setValue, initialData]);

  const safeStudentId = selectedStudentId && !isNaN(selectedStudentId) ? selectedStudentId : '';
  const safeDisciplineId = selectedDisciplineId && !isNaN(selectedDisciplineId) ? selectedDisciplineId : '';
  const safeLevelId = selectedLevelId && !isNaN(selectedLevelId) ? selectedLevelId : '';

  // Obtener estudiante seleccionado
  const { data: selectedStudent } = useQuery(
    ['student', safeStudentId],
    async () => {
      const response = await api.get(`/students/${safeStudentId}`);
      return response.data;
    },
    { enabled: !!safeStudentId && safeStudentId !== '' && !isNaN(safeStudentId) }
  );

  // Obtener historial completo de matrículas del alumno seleccionado
  const { data: studentEnrollments } = useQuery(
    ['student-enrollments-history', safeStudentId],
    async () => {
      if (!safeStudentId || safeStudentId === '' || isNaN(safeStudentId)) return [];
      const response = await api.get('/enrollments', {
        params: { student_id: safeStudentId }
      });
      return response.data;
    },
    { enabled: !!safeStudentId && safeStudentId !== '' && !isNaN(safeStudentId) && !initialData }
  );

  // Matrículas verdaderamente activas: estado 'active' y fecha fin no vencida
  const trueActiveEnrollments = React.useMemo(() => {
    if (!studentEnrollments) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return studentEnrollments.filter(e => {
      if (e.status !== 'active') return false;
      if (!e.end_date) return true;
      const end = new Date(e.end_date + 'T00:00:00');
      return end >= today;
    });
  }, [studentEnrollments]);

  // Obtener disciplinas
  const { data: disciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  // Obtener niveles raíz de la disciplina (categorías y niveles simples)
  const { data: rootLevels } = useQuery(
    ['root-levels', safeDisciplineId],
    async () => {
      if (!safeDisciplineId || safeDisciplineId === '' || isNaN(safeDisciplineId)) return [];
      const response = await api.get('/levels', {
        params: { discipline_id: safeDisciplineId, only_root: true, is_active: true }
      });
      return response.data;
    },
    { 
      enabled: !!safeDisciplineId && safeDisciplineId !== '' && !isNaN(safeDisciplineId),
      keepPreviousData: true,
      staleTime: 30000 // Mantener datos en caché por 30 segundos
    }
  );

  // Separar categorías y niveles simples, ordenados por order
  const categories = React.useMemo(() => {
    if (!rootLevels) return [];
    return rootLevels
      .filter(level => level.category_type === 'age')
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [rootLevels]);

  const simpleLevels = React.useMemo(() => {
    if (!rootLevels) return [];
    return rootLevels
      .filter(level => level.category_type === 'skill' && !level.parent_level_id)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [rootLevels]);

  // Obtener subniveles de la categoría seleccionada, ordenados por order
  const { data: sublevels } = useQuery(
    ['sublevels', selectedCategoryId],
    async () => {
      if (!selectedCategoryId) return [];
      const response = await api.get('/levels', {
        params: { parent_level_id: selectedCategoryId, is_active: true }
      });
      // Ordenar por order (el backend ya debería hacerlo, pero por si acaso)
      const sorted = (response.data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      return sorted;
    },
    { 
      enabled: !!selectedCategoryId,
      keepPreviousData: true,
      staleTime: 30000
    }
  );

  // Obtener grupos
  const { data: allGroups } = useQuery('groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return response.data;
  }, {
    keepPreviousData: true,
    staleTime: 30000
  });

  // Cupo real viene del grupo (conteo en servidor, no de las primeras 100 matrículas)
  const groupCapacityInfo = React.useMemo(() => {
    if (!allGroups) return {};
    
    const capacityMap = {};
    allGroups.forEach(group => {
      const enrollmentsCount = Number(group.active_enrollments) || 0;
      const capacity = group.capacity || 20;
      const available = Math.max(0, capacity - enrollmentsCount);
      
      capacityMap[group.id] = {
        total: capacity,
        enrolled: enrollmentsCount,
        available: available,
        isFull: available === 0
      };
    });
    
    return capacityMap;
  }, [allGroups]);

  // Obtener IDs de grupos donde el estudiante ya tiene matrícula vigente
  // Bloquea: active, pending, suspended con end_date >= hoy o sin end_date
  const enrolledGroupIds = React.useMemo(() => {
    if (!studentEnrollments || !safeStudentId) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return studentEnrollments
      .filter(enrollment => {
        if (!enrollment.is_active) return false;
        if (!['active', 'pending', 'suspended'].includes(enrollment.status)) return false;
        if (!enrollment.end_date) return true;
        const end = new Date(enrollment.end_date + 'T00:00:00');
        return end >= today;
      })
      .map(enrollment => enrollment.group_id);
  }, [studentEnrollments, safeStudentId]);

  // Filtrar grupos por disciplina y nivel seleccionados
  const filteredGroups = React.useMemo(() => {
    if (!allGroups) return [];
    if (!safeDisciplineId || !safeLevelId) return [];
    
    return allGroups.filter(group => 
      group.discipline_id === parseInt(safeDisciplineId) &&
      group.level_id === parseInt(safeLevelId) &&
      group.is_active === true &&
      !enrolledGroupIds.includes(group.id)
    );
  }, [allGroups, safeDisciplineId, safeLevelId, enrolledGroupIds]);

  // Agrupar grupos por sede
  const groupsByLocation = React.useMemo(() => {
    const grouped = {};
    
    filteredGroups.forEach(group => {
      const locationId = group.location_id || 'sin-sede';
      
      if (!grouped[locationId]) {
        grouped[locationId] = {
          location: group.location || { id: null, name: 'Sin sede asignada', address: null, phone: null, code: null },
          groups: []
        };
      }
      
      grouped[locationId].groups.push(group);
    });
    
    return grouped;
  }, [filteredGroups]);

  // Obtener información del grupo seleccionado
  const selectedGroup = allGroups?.find(g => g.id === parseInt(selectedGroupId || initialData?.group_id));

  // Calcular cuota final según tipo de descuento
  const finalFee = React.useMemo(() => {
    if (!monthlyFee || monthlyFee === '') return 0;
    const fee = parseFloat(monthlyFee);
    
    if (isNaN(fee) || fee < 0) return 0;
    
    if (!hasScholarship) return fee;
    
    if (discountType === 'percentage') {
      if (!discountPercentage || discountPercentage === '') return fee;
      const discount = parseFloat(discountPercentage);
      if (isNaN(discount) || discount <= 0) return fee;
      if (discount > 100) return fee;
      
      const discountValue = fee * discount / 100;
      const final = fee - discountValue;
      return Math.max(final, 0);
    } else if (discountType === 'fixed_amount') {
      if (!discountAmount || discountAmount === '') return fee;
      const discount = parseFloat(discountAmount);
      if (isNaN(discount) || discount <= 0) return fee;
      
      const final = fee - discount;
      return Math.max(final, 0);
    }
    
    return fee;
  }, [monthlyFee, hasScholarship, discountType, discountPercentage, discountAmount]);

  useEffect(() => {
    if (initialData || !collectPaymentNow) return;
    if (startDate) {
      setValue('payment_due_date', startDate);
    }
  }, [startDate, collectPaymentNow, setValue, initialData]);

  useEffect(() => {
    if (initialData || !collectPaymentNow) return;
    const feeStr = finalFee > 0 ? Number(finalFee).toFixed(2) : '';
    setValue('payment_amount', feeStr);
    setValue('payment_paid_amount', feeStr);
  }, [finalFee, collectPaymentNow, setValue, initialData]);

  useEffect(() => {
    if (!initialData && monthlyFee !== '' && finalFee <= 0 && collectPaymentNow) {
      setValue('collect_payment_now', false);
    }
  }, [collectPaymentNow, finalFee, initialData, monthlyFee, setValue]);

  // Resetear selecciones cuando cambia la disciplina
  useEffect(() => {
    if (safeDisciplineId && !initialData) {
      setSelectedCategoryId(null);
      setValue('level_id', '');
      setValue('group_id', '');
    }
  }, [safeDisciplineId, setValue, initialData]);

  // Cargar datos iniciales para edición
  useEffect(() => {
    if (initialData && selectedGroup) {
      if (selectedGroup.discipline_id) {
        setValue('discipline_id', selectedGroup.discipline_id);
      }
      if (initialData.level_id) {
        setValue('level_id', initialData.level_id);
      }
      if (initialData.group_id) {
        setValue('group_id', initialData.group_id);
      }
    }
  }, [initialData, selectedGroup, setValue]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') {
      return 'S/ 0.00';
    }
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numAmount) || numAmount < 0) {
      return 'S/ 0.00';
    }
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(numAmount);
  };

  // Obtener estudiante seleccionado para edad y validaciones
  const studentAge = selectedStudent ? calculateAge(selectedStudent.birth_date) : null;
  const studentBirthYear = selectedStudent ? getBirthYear(selectedStudent.birth_date) : null;

  // Helper para manejar selección de categoría
  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setValue('level_id', '');
    setValue('group_id', '');
  };

  // Helper para manejar selección de nivel simple (sin categoría)
  const handleSimpleLevelSelect = (levelId) => {
    setSelectedCategoryId(null);
    setValue('level_id', levelId);
    setValue('group_id', '');
  };

  // Helper para manejar selección de subnivel
  const handleSublevelSelect = (sublevelId) => {
    setValue('level_id', sublevelId);
    setValue('group_id', '');
  };

  // Obtener categoría seleccionada
  const selectedCategory = categories?.find(c => c.id === selectedCategoryId);

  // Obtener disciplina seleccionada
  const selectedDiscipline = React.useMemo(() => {
    if (!safeDisciplineId) return null;
    return disciplines?.find(d => d.id === parseInt(safeDisciplineId));
  }, [safeDisciplineId, disciplines]);

  // Obtener nivel seleccionado
  const selectedLevel = React.useMemo(() => {
    if (!safeLevelId) return null;
    // Buscar en subniveles
    if (sublevels) {
      const sublevel = sublevels.find(s => s.id === parseInt(safeLevelId));
      if (sublevel) return sublevel;
    }
    // Buscar en niveles simples
    if (simpleLevels) {
      const simple = simpleLevels.find(s => s.id === parseInt(safeLevelId));
      if (simple) return simple;
    }
    return null;
  }, [safeLevelId, sublevels, simpleLevels]);

  const handleFormSubmit = (data) => {
    if (!initialData && data.group_id && enrolledGroupIds.includes(parseInt(data.group_id))) {
      const conflicting = studentEnrollments?.find(
        e => e.group_id === parseInt(data.group_id) &&
             ['active', 'pending', 'suspended'].includes(e.status)
      );
      const groupName = conflicting?.group?.name || `Grupo #${data.group_id}`;
      toast.error(`No se puede crear la matrícula: el alumno ya tiene una matrícula vigente en "${groupName}". Para renovar, espera a que venza la matrícula actual.`, {
        autoClose: 5000
      });
      return;
    }
    
    // Limpiar campos de descuento: eliminar si están vacíos
    const cleanedData = { ...data };
    
    // Convertir y validar discount_percentage
    if (data.discount_percentage === '' || data.discount_percentage === null || data.discount_percentage === undefined) {
      delete cleanedData.discount_percentage;
    } else {
      cleanedData.discount_percentage = parseFloat(data.discount_percentage);
    }
    
    // Convertir y validar discount_amount
    if (data.discount_amount === '' || data.discount_amount === null || data.discount_amount === undefined) {
      delete cleanedData.discount_amount;
    } else {
      cleanedData.discount_amount = parseFloat(data.discount_amount);
    }
    
    // Limpiar scholarship_name
    if (data.scholarship_name?.trim()) {
      cleanedData.scholarship_name = data.scholarship_name.trim();
    } else {
      delete cleanedData.scholarship_name;
    }
    
    // Limpiar notes
    if (data.notes?.trim()) {
      cleanedData.notes = data.notes.trim();
    } else {
      delete cleanedData.notes;
    }

    if (!initialData) {
      cleanedData.collect_payment_now = !!data.collect_payment_now && finalFee > 0;
      if (cleanedData.collect_payment_now) {
        const paid = parseFloat(data.payment_paid_amount);
        const amount = parseFloat(data.payment_amount);
        if (Number.isNaN(paid) || paid <= 0) {
          toast.error('Si cobras ahora, indica un monto pagado mayor a 0.');
          return;
        }
        if (!Number.isNaN(amount) && paid > amount) {
          toast.error('El monto cobrado no puede ser mayor al monto del pago.');
          return;
        }
        cleanedData.initial_payment = {
          payment_date: data.payment_date || formatTodayLocal(),
          due_date: data.payment_due_date || data.start_date,
          amount: !Number.isNaN(amount) && amount > 0 ? amount : paid,
          paid_amount: paid,
          payment_method: data.payment_method || null,
          reference_number: data.payment_reference_number?.trim() || null,
        };
      }
    }

    delete cleanedData.payment_date;
    delete cleanedData.payment_due_date;
    delete cleanedData.payment_amount;
    delete cleanedData.payment_paid_amount;
    delete cleanedData.payment_method;
    delete cleanedData.payment_reference_number;
    delete cleanedData.discipline_id;
    
    onSubmit(cleanedData);
  };

  return (
    <>
    <form onSubmit={handleSubmit(handleFormSubmit)} className="enrollment-form">
      {/* Paso 1: Seleccionar Estudiante */}
      <div className="form-group">
        <label htmlFor="student_id">Estudiante *</label>
        {initialData ? (
          <input
            type="text"
            value={selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name}` : `ID: ${initialData.student_id}`}
            disabled
            className=""
          />
        ) : (
          <StudentSearchSelect
            value={safeStudentId}
            hasError={!!errors.student_id}
            showAddNew
            onAddNew={() => setShowAddStudentModal(true)}
            onChange={(studentId) => {
              setValue('student_id', studentId || '');
            }}
            hiddenInputProps={register('student_id', {
              required: 'El estudiante es obligatorio',
              valueAsNumber: true,
            })}
          />
        )}
        {errors.student_id && <span className="error-message">{errors.student_id.message}</span>}

        {/* Warning: alumno ya tiene matrícula activa y vigente */}
        {trueActiveEnrollments.length > 0 && !initialData && (
          <div className="active-enrollment-warning">
            <span>⚠️</span>
            <div>
              <strong>Este alumno ya tiene {trueActiveEnrollments.length} matrícula(s) activa(s) y vigente(s):</strong>
              <ul>
                {trueActiveEnrollments.map(e => (
                  <li key={e.id}>
                    {e.group?.discipline?.name || e.group?.discipline_name || 'Deporte'}
                    {e.group?.name ? ` — ${e.group.name}` : ''}
                    {e.end_date ? ` (hasta ${(() => { const [y,m,d] = e.end_date.split('-'); return `${d}/${m}/${y}`; })()})` : ''}
                  </li>
                ))}
              </ul>
              <span>Puedes continuar para matricularlo en otro deporte/grupo.</span>
            </div>
          </div>
        )}

        {selectedStudent && (
          <div className="student-info-box">
            <strong>Información del estudiante:</strong>
            <span>Edad: {studentAge !== null ? `${studentAge} años` : 'N/A'}</span>
            <span>Año de nacimiento: {studentBirthYear || 'N/A'}</span>
          </div>
        )}

        {/* Historial de matrículas del alumno */}
        {studentEnrollments && studentEnrollments.length > 0 && !initialData && (
          <div className="enrollment-history">
            <div className="enrollment-history-title">📋 Historial de matrículas</div>
            <div className="enrollment-history-cards">
              {[...studentEnrollments]
                .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                .map(e => {
                  const statusMap = {
                    active: { label: 'Activa', color: '#28a745' },
                    suspended: { label: 'Suspendida', color: '#ff9800' },
                    cancelled: { label: 'Cancelada', color: '#dc3545' },
                    completed: { label: 'Completada', color: '#17a2b8' },
                  };
                  const st = statusMap[e.status] || { label: e.status, color: '#888' };
                  const today = new Date();
                  today.setHours(0,0,0,0);
                  const endDate = e.end_date ? new Date(e.end_date + 'T00:00:00') : null;
                  const isVigente = e.status === 'active' && (!endDate || endDate >= today);
                  const fmt = (d) => { if (!d) return '-'; const [y,m,dy] = d.split('-'); return `${dy}/${m}/${y}`; };
                  return (
                    <div key={e.id} className={`enrollment-history-card ${isVigente ? 'vigente' : ''}`}>
                      <div className="ehc-header">
                        <span className="ehc-discipline">{e.group?.discipline?.name || 'Deporte'}</span>
                        <span className="ehc-status" style={{ color: st.color }}>{st.label}{isVigente ? ' ✓' : ''}</span>
                      </div>
                      <div className="ehc-group">{e.group?.name || `Grupo #${e.group_id}`}</div>
                      <div className="ehc-dates">
                        <span>📅 {fmt(e.start_date)} → {fmt(e.end_date)}</span>
                      </div>
                      {e.monthly_fee > 0 && (
                        <div className="ehc-fee">S/ {parseFloat(e.final_monthly_fee || e.monthly_fee).toFixed(2)}/mes</div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Paso 2: Seleccionar Disciplina */}
      <div className="form-group">
        <label htmlFor="discipline_id">Disciplina *</label>
        <select
          id="discipline_id"
          {...register('discipline_id', { 
            required: 'La disciplina es obligatoria',
            setValueAs: (value) => value === '' ? '' : parseInt(value)
          })}
          className={errors.discipline_id ? 'input-error' : ''}
          disabled={!!initialData}
        >
          <option value="">Seleccionar disciplina...</option>
          {disciplines?.map((discipline) => (
            <option key={discipline.id} value={discipline.id}>
              {discipline.icon || '⚽'} {discipline.name}
            </option>
          ))}
        </select>
        {errors.discipline_id && <span className="error-message">{errors.discipline_id.message}</span>}
      </div>

      {/* Paso 3: Seleccionar Categoría o Nivel */}
      {safeDisciplineId && !initialData && (
        <div className="form-group">
          <label>Categoría / Nivel *</label>
          
          {/* Categorías por edad */}
          {categories && categories.length > 0 && (
            <div className="category-section">
              <h4 className="section-subtitle">📂 Categorías por Edad</h4>
              <div className="category-cards">
                {categories.map((category) => {
                  const compatibility = selectedStudent ? isCategoryCompatible(selectedStudent, category) : { compatible: true };
                  const isSelected = selectedCategoryId === category.id;
                  const categoryInfo = formatCategoryInfo(category);
                  
                  return (
                    <div
                      key={category.id}
                      className={`category-card ${isSelected ? 'selected' : ''} ${!compatibility.compatible ? 'incompatible' : ''}`}
                      onClick={() => {
                        if (compatibility.compatible) {
                          handleCategorySelect(category.id);
                        } else {
                          setAgeOverrideConfirm({ category, reason: compatibility.reason });
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="category-card-header">
                        <strong>{category.name}</strong>
                        {isSelected && <span className="selected-badge">✓</span>}
                      </div>
                      {categoryInfo && (
                        <div className="category-card-info">{categoryInfo}</div>
                      )}
                      {category.sublevels && category.sublevels.length > 0 && (
                        <div className="category-card-sublevels">
                          {category.sublevels.length} subnivel(es) disponibles
                        </div>
                      )}
                      {!compatibility.compatible && (
                        <div className="incompatibility-warning">
                          ⚠️ Fuera de rango de edad — click para continuar igualmente
                        </div>
                      )}
                      {compatibility.compatible && selectedStudent && (
                        <div className="compatibility-ok">✅ Compatible</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Niveles simples (sin categoría) */}
          {simpleLevels && simpleLevels.length > 0 && (
            <div className="simple-levels-section">
              <h4 className="section-subtitle">🎯 Niveles de Habilidad (Sin categoría de edad)</h4>
              <div className="simple-level-cards">
                {simpleLevels.map((level) => {
                  const isSelected = safeLevelId === String(level.id) && !selectedCategoryId;
                  
                  return (
                    <div
                      key={level.id}
                      className={`simple-level-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSimpleLevelSelect(level.id)}
                    >
                      <strong>{level.name}</strong>
                      {isSelected && <span className="selected-badge">✓</span>}
                      <div className="simple-level-info">Disponible para todas las edades</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!categories || categories.length === 0) && (!simpleLevels || simpleLevels.length === 0) && (
            <ErrorState
              type="warning"
              title="No hay categorías ni niveles disponibles"
              message={`La disciplina "${selectedDiscipline?.name || 'seleccionada'}" no tiene niveles configurados.`}
              diagnosis="Para poder crear matrículas necesitas:"
              reasons={[
                'Crear al menos un nivel para esta disciplina',
                'Los niveles pueden ser categorías por edad o niveles de habilidad'
              ]}
              actions={[
                {
                  label: 'Crear Nivel',
                  primary: true,
                  onClick: () => {
                    const params = new URLSearchParams();
                    if (selectedDiscipline?.id) params.set('discipline_id', selectedDiscipline.id);
                    params.set('action', 'create');
                    navigate(`/levels?${params.toString()}`);
                  }
                },
                {
                  label: 'Ver Disciplinas',
                  primary: false,
                  onClick: () => navigate('/disciplines')
                }
              ]}
              className="compact"
            />
          )}
        </div>
      )}

      {/* Paso 3.5: Seleccionar Subnivel (si se seleccionó una categoría) */}
      {selectedCategoryId && !initialData && (
        <div className="form-group">
          <label>Nivel de Habilidad *</label>
          <div className="selected-category-info">
            <strong>Categoría seleccionada:</strong> {selectedCategory?.name}
            {formatCategoryInfo(selectedCategory) && (
              <span className="category-details">({formatCategoryInfo(selectedCategory)})</span>
            )}
          </div>
          
          {sublevels && sublevels.length > 0 ? (
            <div className="sublevel-cards">
              {sublevels.map((sublevel) => {
                const isSelected = safeLevelId === String(sublevel.id);
                
                return (
                  <div
                    key={sublevel.id}
                    className={`sublevel-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSublevelSelect(sublevel.id)}
                  >
                    <strong>{sublevel.name}</strong>
                    {isSelected && <span className="selected-badge">✓</span>}
                    {sublevel.description && (
                      <div className="sublevel-description">{sublevel.description}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state-message">
              Esta categoría no tiene subniveles de habilidad.
              <button 
                type="button" 
                className="btn btn-link"
                onClick={() => setSelectedCategoryId(null)}
              >
                Seleccionar otra categoría
              </button>
            </div>
          )}
        </div>
      )}

      {/* Campo oculto para level_id */}
      <input
        type="hidden"
        {...register('level_id', { 
          required: 'Debes seleccionar un nivel',
          valueAsNumber: true
        })}
      />
      {errors.level_id && <span className="error-message">{errors.level_id.message}</span>}

      {/* Campo oculto para group_id */}
      <input
        type="hidden"
        {...register('group_id', { 
          required: 'Debes seleccionar un grupo',
          valueAsNumber: true
        })}
      />
      {errors.group_id && <span className="error-message">{errors.group_id.message}</span>}

      {/* Paso 4: Seleccionar Grupo */}
      {safeDisciplineId && safeLevelId && !initialData && (
        <div className="form-group">
          <label>Seleccionar Grupo y Sede *</label>
          {!safeStudentId || safeStudentId === '' ? (
            <ErrorState
              type="info"
              icon="ℹ️"
              title="Selecciona primero un estudiante"
              message="Para ver los grupos disponibles, primero debes seleccionar un estudiante."
              className="compact"
            />
          ) : Object.keys(groupsByLocation).length === 0 ? (
            <ErrorState
              type="warning"
              title="No hay grupos disponibles"
              message="No se encontraron grupos para esta combinación:"
              details={[
                `Disciplina: ${selectedDiscipline?.name || 'No seleccionada'}`,
                `Nivel: ${selectedLevel?.name || 'No seleccionado'}`
              ]}
              diagnosis="Esto puede deberse a que:"
              reasons={diagnoseMissingGroups(
                selectedDiscipline,
                selectedLevel,
                allGroups,
                enrolledGroupIds
              )}
              actions={[
                {
                  label: 'Crear Grupo',
                  primary: true,
                  onClick: () => {
                    const params = new URLSearchParams();
                    if (selectedDiscipline?.id) params.set('discipline_id', selectedDiscipline.id);
                    if (selectedLevel?.id) params.set('level_id', selectedLevel.id);
                    params.set('action', 'create');
                    navigate(`/groups?${params.toString()}`);
                  }
                },
                {
                  label: 'Ver Todos los Grupos',
                  primary: false,
                  onClick: () => navigate('/groups')
                }
              ]}
              className="compact"
            />
          ) : (
            <div className="groups-by-location">
              {Object.values(groupsByLocation).map((locationData, index) => {
                const location = locationData.location;
                const groups = locationData.groups;
                
                return (
                  <div key={location.id || `sin-sede-${index}`} className="location-group-card">
                    <div className="location-header">
                      <h4>📍 {location.name} {location.code ? `(${location.code})` : ''}</h4>
                      {location.address && <p className="location-address">📍 {location.address}</p>}
                      {location.phone && <p className="location-phone">📞 {location.phone}</p>}
                    </div>
                    <div className="groups-list">
                      {groups.map((group) => {
                        const isSelected = parseInt(selectedGroupId) === group.id;
                        const getDayName = (dayOfWeek) => {
                          const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
                          return days[dayOfWeek] || 'N/A';
                        };
                        
                        const capacityInfo = groupCapacityInfo[group.id] || { 
                          total: group.capacity || 20, 
                          enrolled: 0, 
                          available: group.capacity || 20, 
                          isFull: false 
                        };
                        
                        return (
                          <div
                            key={group.id}
                            className={`group-card ${isSelected ? 'selected' : ''} ${capacityInfo.isFull ? 'group-full' : ''}`}
                            onClick={() => {
                              if (!capacityInfo.isFull) {
                                setValue('group_id', group.id);
                              }
                            }}
                            style={{ 
                              cursor: capacityInfo.isFull ? 'not-allowed' : 'pointer',
                              opacity: capacityInfo.isFull ? 0.6 : 1
                            }}
                          >
                            <div className="group-card-header">
                              <h5>{group.name}</h5>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {isSelected && <span className="selected-badge">✓ Seleccionado</span>}
                                {capacityInfo.isFull && <span className="full-badge">🔒 Lleno</span>}
                              </div>
                            </div>
                            {group.schedule && (
                              <div className="group-schedule">
                                <span className="schedule-day">📅 {getDayName(group.schedule.day_of_week)}</span>
                                <span className="schedule-time">
                                  ⏰ {group.schedule.start_time} - {group.schedule.end_time}
                                </span>
                              </div>
                            )}
                            {group.teacher && (
                              <div className="group-teacher">
                                👤 Prof. {group.teacher.full_name}
                              </div>
                            )}
                            <div className="capacity-info" style={{ 
                              color: capacityInfo.isFull ? '#dc3545' : capacityInfo.available <= 3 ? '#ff9800' : '#28a745',
                              fontWeight: 'bold',
                              marginTop: '8px',
                              fontSize: '13px'
                            }}>
                              👥 Capacidad: {capacityInfo.enrolled}/{capacityInfo.total} ({capacityInfo.available} disponibles)
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Resto del formulario: Fechas, Pagos, etc. */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="start_date">Fecha de Inicio *</label>
          <input
            type="date"
            id="start_date"
            {...register('start_date', { required: 'La fecha de inicio es obligatoria' })}
            className={errors.start_date ? 'input-error' : ''}
          />
          {errors.start_date && <span className="error-message">{errors.start_date.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="end_date">Fecha de Fin *</label>
          <input
            type="date"
            id="end_date"
            {...register('end_date', { required: 'La fecha de fin es obligatoria' })}
            className={errors.end_date ? 'input-error' : ''}
          />
          {errors.end_date && <span className="error-message">{errors.end_date.message}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="period_type">Tipo de Período *</label>
        <select
          id="period_type"
          {...register('period_type', { required: 'El tipo de período es obligatorio' })}
          className={errors.period_type ? 'input-error' : ''}
        >
          <option value="monthly">Mensual (1 mes)</option>
          <option value="quarterly">Trimestral (3 meses)</option>
          <option value="annual">Anual (1 año)</option>
        </select>
        {errors.period_type && <span className="error-message">{errors.period_type.message}</span>}
        <span className="info-message">ℹ️ La fecha de fin se calculará automáticamente según el tipo de período seleccionado</span>
      </div>

      <div className="form-group">
        <label htmlFor="monthly_fee">Cuota Mensual (S/) *</label>
        <input
          type="number"
          id="monthly_fee"
          step="0.01"
          {...register('monthly_fee', { 
            required: 'La cuota mensual es obligatoria',
            min: { value: 0, message: 'La cuota debe ser mayor o igual a 0' }
          })}
          className={errors.monthly_fee ? 'input-error' : ''}
          placeholder="0.00"
          onWheel={(e) => e.target.blur()}
        />
        {errors.monthly_fee && <span className="error-message">{errors.monthly_fee.message}</span>}
      </div>

      {/* Sistema de Becas y Descuentos */}
      <div className="scholarship-section">
        <div className="scholarship-header">
          <label className="checkbox-label">
            <input
              type="checkbox"
              {...register('has_scholarship')}
              onChange={(e) => {
                setValue('has_scholarship', e.target.checked);
                if (!e.target.checked) {
                  setValue('scholarship_name', '');
                  setValue('discount_percentage', '');
                  setValue('discount_amount', '');
                }
              }}
            />
            <span>🎓 Aplicar Beca o Descuento</span>
          </label>
        </div>

        {hasScholarship && (
          <div className="scholarship-details">
            <div className="form-group">
              <label htmlFor="scholarship_name">Nombre de la Beca/Descuento</label>
              <input
                type="text"
                id="scholarship_name"
                {...register('scholarship_name')}
                placeholder="Ej: Beca Deportiva, Descuento Familiar, etc."
              />
              <small className="field-hint">Opcional: Identifica el tipo de beca o descuento</small>
            </div>

            <div className="form-group">
              <label htmlFor="discount_type">Tipo de Descuento</label>
              <select
                id="discount_type"
                {...register('discount_type')}
                onChange={(e) => {
                  setValue('discount_type', e.target.value);
                  setValue('discount_percentage', '');
                  setValue('discount_amount', '');
                }}
              >
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed_amount">Monto Fijo (S/)</option>
              </select>
            </div>

            {discountType === 'percentage' ? (
              <div className="form-group">
                <label htmlFor="discount_percentage">Porcentaje de Descuento (%)</label>
                <input
                  type="number"
                  id="discount_percentage"
                  step="0.01"
                  {...register('discount_percentage', { 
                    min: { value: 0, message: 'El descuento debe ser mayor o igual a 0' },
                    max: { value: 100, message: 'El descuento no puede ser mayor a 100%' }
                  })}
                  className={errors.discount_percentage ? 'input-error' : ''}
                  placeholder="0"
                  onWheel={(e) => e.target.blur()}
                />
                {errors.discount_percentage && <span className="error-message">{errors.discount_percentage.message}</span>}
                <small className="field-hint">Ingresa un valor entre 0 y 100</small>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="discount_amount">Monto de Descuento (S/)</label>
                <input
                  type="number"
                  id="discount_amount"
                  step="0.01"
                  {...register('discount_amount', { 
                    min: { value: 0, message: 'El descuento debe ser mayor o igual a 0' }
                  })}
                  className={errors.discount_amount ? 'input-error' : ''}
                  placeholder="0.00"
                  onWheel={(e) => e.target.blur()}
                />
                {errors.discount_amount && <span className="error-message">{errors.discount_amount.message}</span>}
                <small className="field-hint">Ingresa el monto fijo a descontar</small>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resumen de cuota */}
      <div className="fee-summary">
        <div className="fee-row">
          <span>Cuota Base:</span>
          <span>{formatCurrency(monthlyFee || 0)}</span>
        </div>
        {hasScholarship && (
          <div className="fee-row discount">
            <span>
              Descuento {discountType === 'percentage' 
                ? `(${discountPercentage || 0}%)` 
                : '(Monto Fijo)'}:
            </span>
            <span>
              -{formatCurrency(
                discountType === 'percentage'
                  ? (parseFloat(monthlyFee || 0) * parseFloat(discountPercentage || 0)) / 100
                  : parseFloat(discountAmount || 0)
              )}
            </span>
          </div>
        )}
        <div className="fee-row total">
          <span>Cuota Final:</span>
          <span>{formatCurrency(finalFee)}</span>
        </div>
      </div>

      {!initialData && (
        <div className="scholarship-section initial-payment-section">
          <div className="scholarship-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                {...register('collect_payment_now')}
                onChange={(e) => setValue('collect_payment_now', e.target.checked)}
              />
              <span>💳 Cobrar matrícula ahora (primer pago)</span>
            </label>
          </div>
          {collectPaymentNow && (
            <div className="scholarship-details">
              <span className="info-message">
                Al guardar se creará la matrícula y el pago en un solo paso. La matrícula quedará activa si el monto cobrado es mayor a 0.
              </span>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="payment_date">Fecha de cobro *</label>
                  <input
                    type="date"
                    id="payment_date"
                    {...register('payment_date', { required: collectPaymentNow })}
                    className={errors.payment_date ? 'input-error' : ''}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="payment_due_date">Vencimiento *</label>
                  <input
                    type="date"
                    id="payment_due_date"
                    {...register('payment_due_date', { required: collectPaymentNow })}
                    className={errors.payment_due_date ? 'input-error' : ''}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="payment_amount">Monto del pago (S/) *</label>
                  <input
                    type="number"
                    id="payment_amount"
                    step="0.01"
                    {...register('payment_amount', {
                      required: collectPaymentNow,
                      min: { value: 0, message: 'Monto inválido' },
                    })}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="payment_paid_amount">Monto cobrado ahora (S/) *</label>
                  <input
                    type="number"
                    id="payment_paid_amount"
                    step="0.01"
                    {...register('payment_paid_amount', {
                      required: collectPaymentNow,
                      min: { value: 0.01, message: 'Debe ser mayor a 0' },
                    })}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="payment_method">Método de pago</label>
                  <select id="payment_method" {...register('payment_method')}>
                    <option value="cash">Efectivo</option>
                    <option value="card">Tarjeta</option>
                    <option value="transfer">Transferencia</option>
                    <option value="yape">Yape</option>
                    <option value="plin">Plin</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="payment_reference_number">Nº operación / referencia</label>
                  <input
                    type="text"
                    id="payment_reference_number"
                    {...register('payment_reference_number')}
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <div className="fee-summary">
                <div className="fee-row total">
                  <span>Cuota final a cobrar:</span>
                  <span>{formatCurrency(finalFee)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="notes">Notas</label>
        <textarea
          id="notes"
          {...register('notes')}
          rows="3"
          placeholder="Notas adicionales sobre la matrícula..."
        />
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading
            ? 'Guardando...'
            : initialData
              ? 'Actualizar'
              : collectPaymentNow
                ? 'Crear matrícula y cobrar'
                : 'Crear matrícula'}
        </button>
      </div>
    </form>

    {/* Modal de confirmación: categoría fuera de rango de edad */}
    <Modal
      isOpen={!!ageOverrideConfirm}
      onClose={() => setAgeOverrideConfirm(null)}
      title="⚠️ Categoría fuera de rango de edad"
      closeOnOverlayClick={true}
    >
      {ageOverrideConfirm && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ margin: 0, fontSize: '15px', color: '#2c3e50' }}>
            El alumno <strong>{selectedStudent?.first_name} {selectedStudent?.last_name}</strong> tiene{' '}
            <strong>{studentAge} años</strong>, pero la categoría{' '}
            <strong>"{ageOverrideConfirm.category.name}"</strong> está diseñada para otro rango de edad.
          </p>
          <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '12px', fontSize: '13px', color: '#856404' }}>
            <strong>Motivo: </strong>{ageOverrideConfirm.reason}
          </div>
          <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>
            Puedes continuar si el entrenador o coordinador aprobó esta excepción (por ejemplo, un alumno avanzado promovido a una categoría superior).
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '8px' }}>
            <button
              className="btn btn-cancel"
              onClick={() => setAgeOverrideConfirm(null)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              style={{ background: '#e67e22', borderColor: '#e67e22' }}
              onClick={() => {
                handleCategorySelect(ageOverrideConfirm.category.id);
                setAgeOverrideConfirm(null);
              }}
            >
              Sí, continuar de todas formas
            </button>
          </div>
        </div>
      )}
    </Modal>

    {/* Modal para registrar nuevo alumno */}
    <Modal
      isOpen={showAddStudentModal}
      onClose={() => setShowAddStudentModal(false)}
      title="➕ Registrar Nuevo Alumno"
      closeOnOverlayClick={false}
      size="xl"
    >
      <StudentForm
        onSubmit={(data) => createStudentMutation.mutate(data)}
        onCancel={() => setShowAddStudentModal(false)}
        isLoading={createStudentMutation.isLoading}
      />
    </Modal>
    </>
  );
};

export default EnrollmentForm;
